import { useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { BsPlusLg, BsTrash } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import CollectionToolbar from '#globalComponents/CollectionView/CollectionToolbar';
import CollectionView from '#globalComponents/CollectionView/CollectionView';
import useCollectionSearch from '#globalComponents/CollectionView/useCollectionSearch';
import useCollectionViewMode from '#globalComponents/CollectionView/useCollectionViewMode';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';

import CreatePostMailingCampaignModal from '../../components/campaigns/CreatePostMailingCampaignModal';
import PostMailingQueryError from '../../components/campaigns/PostMailingQueryError';
import { getCampaignStatusTag } from '../../constants/campaignStatuses';
import { useDeletePostMailingCampaign, useUpdatePostMailingCampaign } from '../../models/campaigns/mutations';
import { usePostMailingCampaigns } from '../../models/campaigns/queries';

const DRAFT_STATUSES = ['draft'];
const ACTIVE_STATUSES = [
	'awaiting_offer',
	'awaiting_payment',
	'payment_processing',
	'sending',
	'needs_review',
	'ready_to_distribute',
];
const DONE_STATUSES = ['completed', 'partially_failed', 'failed', 'cancelled'];

const TABS = [
	{ key: 'all', label: 'Alle' },
	{ key: 'draft', label: 'Entwürfe', statuses: DRAFT_STATUSES },
	{ key: 'active', label: 'Aktiv', statuses: ACTIVE_STATUSES },
	{ key: 'done', label: 'Abgeschlossen', statuses: DONE_STATUSES },
];

const matchesTab = (campaign, tabKey) => {
	const tab = TABS.find((t) => t.key === tabKey);
	if (!tab?.statuses) return true;
	return tab.statuses.includes(campaign.status);
};

const getSearchText = (campaign) => campaign.name ?? '';

const PostMailingOverviewPage = () => {
	const navigate = useNavigate();
	const [viewMode, setViewMode] = useCollectionViewMode('postMailingCampaigns');
	const [activeTab, setActiveTab] = useState('all');
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [campaignToDelete, setCampaignToDelete] = useState(null);

	const { data: campaigns = [], isLoading, isError, error, refetch } = usePostMailingCampaigns();
	const deleteCampaign = useDeletePostMailingCampaign();
	const { mutateAsync: updateCampaign } = useUpdatePostMailingCampaign();
	const { search, setSearch, filteredItems: searchedCampaigns } = useCollectionSearch(campaigns, getSearchText);

	const items = useMemo(
		() =>
			searchedCampaigns
				.filter((c) => matchesTab(c, activeTab))
				.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
				.map((campaign) => {
					const recipientCount = campaign.recipientCount ?? 0;
					const listCount = campaign.postMailingCampaign_contactList?.length ?? 0;
					// Letters held for manual address review; surfaced here because we send no email for it.
					const needsReviewCount = campaign.sendStats?.needsReview ?? 0;
					// The backend only accepts edits and deletes on drafts.
					const isDraft = campaign.status === 'draft';
					return {
						id: campaign.id,
						title: campaign.name,
						onOpen: () => navigate(`/post-mailing/${campaign.id}`),
						onRename: isDraft
							? (name) => updateCampaign({ id: campaign.id, updates: { name } })
							: undefined,
						stats: [
							{ value: recipientCount, label: 'Empfänger' },
							{ value: listCount, label: listCount === 1 ? 'Liste' : 'Listen' },
						],
						tags: [
							getCampaignStatusTag(campaign.status),
							needsReviewCount > 0 && {
								prefix: 'Prüfung nötig',
								label: `${needsReviewCount} ${needsReviewCount === 1 ? 'Brief' : 'Briefe'}`,
								tone: 'caution',
							},
						].filter(Boolean),
						date: { label: 'Letzte Änderung', value: campaign.updatedAt ?? campaign.createdAt },
						actions: isDraft
							? [
									{
										key: 'delete',
										icon: BsTrash,
										label: 'Löschen',
										danger: true,
										disabled: deleteCampaign.isPending,
										onClick: () => setCampaignToDelete(campaign),
									},
								]
							: [],
					};
				}),
		[searchedCampaigns, activeTab, navigate, updateCampaign, deleteCampaign.isPending],
	);

	const tabCounts = useMemo(
		() => ({
			all: campaigns.length,
			draft: campaigns.filter((c) => DRAFT_STATUSES.includes(c.status)).length,
			active: campaigns.filter((c) => ACTIVE_STATUSES.includes(c.status)).length,
			done: campaigns.filter((c) => DONE_STATUSES.includes(c.status)).length,
		}),
		[campaigns],
	);

	const handleConfirmDelete = async () => {
		if (!campaignToDelete) return;
		try {
			await deleteCampaign.mutateAsync(campaignToDelete.id);
			setCampaignToDelete(null);
		} catch {
			// Error toast handled by the global MutationCache.
		}
	};

	return (
		<Page header={<PageHeader breadcrumbs={['Post-Mailing']} />}>
			<CollectionToolbar
				search={search}
				onSearchChange={setSearch}
				searchPlaceholder="Kampagne suchen ..."
				viewMode={viewMode}
				onViewModeChange={setViewMode}
			>
				<Button shape="pill" size="sm" onClick={() => setShowCreateModal(true)}>
					<BsPlusLg className="h-3.5 w-3.5" />
					Neue Kampagne
				</Button>
			</CollectionToolbar>

			<div className="mb-5">
				<div className="inline-flex items-center rounded-control bg-bgBase-300 border border-bgBase-500 p-1 gap-1">
					{TABS.map((tab) => {
						const isActive = activeTab === tab.key;
						return (
							<button
								key={tab.key}
								onClick={() => setActiveTab(tab.key)}
								className={`inline-flex items-center gap-1.5 rounded-control px-3 py-1.5 text-sm font-medium transition-all ${
									isActive ? 'bg-white text-text' : 'text-bgBase-800 hover:text-text'
								}`}
							>
								{tab.label}
								<span
									className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
										isActive ? 'bg-primary-100 text-primary-700' : 'bg-bgBase-400 text-bgBase-800'
									}`}
								>
									{tabCounts[tab.key]}
								</span>
							</button>
						);
					})}
				</div>
			</div>

			{isError ? (
				<div className="border border-bgBase-500 rounded-card bg-white">
					<PostMailingQueryError
						error={error}
						title="Kampagnen konnten nicht geladen werden"
						onRetry={refetch}
					/>
				</div>
			) : (
				<CollectionView
					items={items}
					viewMode={viewMode}
					isLoading={isLoading}
					emptyLabel={
						search || activeTab !== 'all' ? 'Keine Kampagnen entsprechen den Filterkriterien.' : undefined
					}
					createLabel="neue Kampagne erstellen"
					onCreate={() => setShowCreateModal(true)}
				/>
			)}

			<CreatePostMailingCampaignModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} />

			<ConfirmModal
				isOpen={Boolean(campaignToDelete)}
				onClose={() => setCampaignToDelete(null)}
				onConfirm={handleConfirmDelete}
				title="Kampagne löschen"
				message={`Möchten Sie die Kampagne "${campaignToDelete?.name}" wirklich löschen?`}
				confirmText="Kampagne löschen"
				cancelText="Abbrechen"
				isLoading={deleteCampaign.isPending}
			/>
		</Page>
	);
};

export default PostMailingOverviewPage;
